import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';

import Icon from '@components/dataDisplay/Icons/Icon';
import NotiBadge from '@template/Notifications/NotiBadge';
import { fuego } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import { useMemberPermission } from '@hooks/usePermission';
import { currentUserIDState } from '@atom/setting/user';
import { useNotificationSound } from '@hooks/useNotificationSound';
import { chatMessageNotificationState, notificationSettingsState } from '@atom/setting/notification';
import { IchatNotification } from '@typings/notification/notification.interface';
import { chatFloatingOpenState } from '@atom/chat/chatFloating';

const ButtonWrap = styled.div`
  border-radius: var(--buttonBorderRadius);
  transition: background-color 0.3s;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  &:hover {
    background-color: var(--inkColor);
    cursor: var(--hoverCursorURL), pointer;
  }
`;

type Props = {
  color?: string;
};

function ChatBtn({ color }: Props) {
  const setFloatingOpen = useSetRecoilState(chatFloatingOpenState);
  const currentUserId = useRecoilValue(currentUserIDState);
  const notificationSettings = useRecoilValue(notificationSettingsState);

  const memberPermission = useMemberPermission({});
  const { notify } = useNotificationSound({
    volume: notificationSettings.volume,
    soundUrl: notificationSettings.soundUrl,
    enableSound: notificationSettings.soundEnabled && notificationSettings.chatNotification,
    enableBrowserNotification: notificationSettings.systemNotification, // �� 브라우저 알림 활성화
  });

  const handleIconClick = useCallback(() => {
    setFloatingOpen((prev) => !prev);
  }, [setFloatingOpen]);

  // 안읽은 메시지 count --------------------------------------
  const [unreadNumber, setUnreadNumber] = useState<number>(0);
  const [notiData, setNotiData] = useRecoilState(chatMessageNotificationState);
  const prevIDRef = useRef<string | null>(null);

  useEffect(() => {
    if (!currentUserId) return;

    const colRef = fuego.db.collection(
      `${CollectionPath.USERS}/${currentUserId}/${CollectionPath.CHAT}`,
    );

    const unsubscribe = colRef.onSnapshot(
      (snapshot) => {
        const chats = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })) as any[];

        const unreadChats = chats.filter(
          (chat) => chat.unRead && Array.isArray(chat.unRead) && chat.unRead.length > 0,
        );

        const totalUnreadCount = unreadChats.reduce(
          (total, chat) => total + (chat.unRead?.length || 0),
          0,
        );

        setUnreadNumber(totalUnreadCount);

        if (unreadChats.length === 0) {
          setNotiData({ show: false, unRead: [], latestDoc: {} as any, id: '' });
          prevIDRef.current = null;
          return;
        }

        const latestChatData = unreadChats[0];
        const latestID = latestChatData?.unRead?.[0] ?? '';

        const notificationData: IchatNotification = {
          show: true,
          unRead: latestChatData?.unRead || [],
          latestDoc: { ...latestChatData.latestDoc },
          id: latestChatData?.id || '',
        };
        setNotiData(notificationData);

        if (latestID && latestID !== prevIDRef.current) {
          prevIDRef.current = latestID;

          if (notificationSettings.chatNotification) {
            const latestMessage = latestChatData?.latestDoc;
            const cleanText = latestMessage?.contents
              ? latestMessage.contents.replace(/<[^>]*>/g, '').trim()
              : '';

            notify({
              title: totalUnreadCount > 1 ? `새 채팅 메시지 (${totalUnreadCount}개)` : '새 채팅 메시지',
              body:
                cleanText.length > 0
                  ? cleanText.substring(0, 100) + (cleanText.length > 100 ? '...' : '')
                  : '새 채팅 메시지가 도착했습니다.',
              tag: `chat-btn-${latestID}`,
              onClick: () => {
                setFloatingOpen(true);
              },
            });
          }
        }
      },
      (error) => {
        console.error('Chat unread listener error:', error);
      },
    );

    return () => unsubscribe();
  }, [currentUserId, notify, setFloatingOpen, notificationSettings.chatNotification]);
  // 안읽은 메시지 count --------------------------------------

  if (!currentUserId) return null;

  if (memberPermission) {
    return (
      <>
        <ButtonWrap onClick={handleIconClick}>
          <Icon name="Mail20" color={color} />
          <NotiBadge unreadNumber={unreadNumber} />
        </ButtonWrap>

      </>
    );
  } else return null;
}

export default ChatBtn;
